/**
 * Automation accounts feature — Zod schemas.
 *
 * An automation account is a non-interactive admin identity with its
 * own admin_api_keys row (prefix `pureid_auto_`). Responses are validated
 * with the contract-generated schemas in api.generated.ts; this module holds
 * the form, URL and UI-only schemas.
 */

import { z } from "zod";

import { automationAccountListSchema, automationAccountSchema } from "./api.generated";

// RBAC roles a automation account can carry. Matches rbac.Role on
// the backend; the service layer enforces rank (SA cannot
// carry a role stricter than the calling admin's).
export const automationAccountRoleSchema = z.enum([
  "platform_admin",
  "platform_operator",
  "tenant_admin",
  "tenant_operator",
  "tenant_readonly",
]);
export type AutomationAccountRole = z.infer<typeof automationAccountRoleSchema>;

// Status values an automation account can hold, exactly as the contract enumerates them.
export const automationAccountStatusSchema = automationAccountSchema.shape.status;
export type AutomationAccountStatus = z.infer<typeof automationAccountStatusSchema>;

// The response shapes (one account, and a page of them) are the contract's
// AdminAutomationAccountResponse and AdminListAutomationAccountsResponse, generated into
// api.generated.ts by make docs; this module never restates them. `token`, `token_prefix` and
// `token_id` appear only on create/rotate responses, and `grace_expires_at` only on a rotate with a
// grace window.
export { automationAccountListSchema, automationAccountSchema };
export type AutomationAccount = z.infer<typeof automationAccountSchema>;
export type AutomationAccountList = z.infer<typeof automationAccountListSchema>;

// List search — URL-driven filter state. `show_inactive` flips
// the list between "active only" (the default, matches what most
// operators want) and "all" (including soft-deleted rows kept
// for audit continuity). Coerced so the URL can encode it as
// "true"/"false" strings and survive copy-paste.
export const automationAccountsListSearchSchema = z.object({
  show_inactive: z.coerce.boolean().optional(),
});
export type AutomationAccountsListSearch = z.infer<
  typeof automationAccountsListSearchSchema
>;

// Create form input. `expires_in_days` is required by the form
// even though the backend treats 0 as "default" — surfacing an
// explicit number keeps operators honest about rotation cadence.
// Max 365 days matches the server-side cap; min 1 day matches
// the server-side minimum.
export const createAutomationAccountSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .max(40, "Name must be 40 characters or fewer")
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
      "Lowercase letters, digits, and hyphens only (not starting or ending with a hyphen)",
    ),
  description: z
    .string()
    .max(512, "Description must be 512 characters or fewer")
    .optional(),
  role: automationAccountRoleSchema,
  expires_in_days: z
    .number()
    .int()
    .min(1, "Expiry must be at least 1 day")
    .max(365, "Expiry cannot exceed 365 days"),
});
export type CreateAutomationAccountInput = z.infer<typeof createAutomationAccountSchema>;

// Rotate form input. Grace window measured in seconds per the
// backend's RotateAutomationAccountRequest. Zero means "revoke the
// old key immediately" (security-sensitive default).
export const rotateAutomationAccountSchema = z.object({
  grace_seconds: z
    .number()
    .int()
    .min(0, "Grace window cannot be negative")
    .max(24 * 3600, "Grace window cannot exceed 24 hours"),
});
export type RotateAutomationAccountInput = z.infer<typeof rotateAutomationAccountSchema>;

// Update (PATCH) input. Every field is optional — the server
// applies only the fields present in the request body.
//   - name / description: free-text edits (same validation as
//     create).
//   - extend_expires_in_days: BUMPS expires_at to now + N days
//     (NOT set-to-absolute-date); matches the backend's rotation-
//     hygiene primitive.
// The refine gates an empty payload on the client so we don't
// issue a no-op network request.
export const updateAutomationAccountSchema = z
  .object({
    name: z
      .string()
      .min(3, "Name must be at least 3 characters")
      .max(40, "Name must be 40 characters or fewer")
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        "Lowercase letters, digits, and hyphens only (not starting or ending with a hyphen)",
      )
      .optional(),
    description: z
      .string()
      .max(512, "Description must be 512 characters or fewer")
      .optional(),
    extend_expires_in_days: z
      .number()
      .int()
      .min(1, "Extension must be at least 1 day")
      .max(365, "Extension cannot exceed 365 days")
      .optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.description !== undefined ||
      v.extend_expires_in_days !== undefined,
    { message: "At least one field must be changed" },
  );
export type UpdateAutomationAccountInput = z.infer<
  typeof updateAutomationAccountSchema
>;
